import type { CustomValidator, RJSFSchema, UiSchema } from '@rjsf/utils';

import { BLOCK_TYPES } from '@/components/ui/richEditor/constants.richEditor';
import { isJson } from '@/components/ui/richEditor/utils';
import {
  UI_FIELD,
  UI_OPTIONS,
  UI_ORDER,
  WIDGET_TYPE_FILE_UPLOAD
} from '../../../constant/formWriter';

interface ValidateUpload {
  name: string;
  required: boolean;
  formData: any;
  errors: [];
}

const validateUpload = ({
  name,
  required,
  formData,
  errors
}: ValidateUpload) => {
  if (formData && formData[name]) {
    const fileData = formData[name];
    // schema validation will throw an error if it is required and not set but
    // will not throw and error if it is set to an emtpy array so we have to check that manually
    if (required && fileData.length === 0) {
      errors[name].addError('at least one file must be uploaded');
    }
    // make sure there are no files in an uploading state
    if (fileData.some(file => file.uploading === true)) {
      errors[name].addError('all files must complete uploading');
    }
    // make sure there are no files that failed to upload
    if (fileData.some(file => file.error === true)) {
      errors[name].addError('failed files must be removed');
    }
  }
};

// Extracts the text of the subject line of a rich text value
// (the slate node with type "subject")
const getSubjectText = (value: unknown): string => {
  if (typeof value !== 'string' || !isJson(value)) {
    return '';
  }
  const nodes = JSON.parse(value);
  if (!Array.isArray(nodes)) {
    return '';
  }
  const subject = nodes.find(node => node?.type === BLOCK_TYPES.SUBJECT);
  if (!Array.isArray(subject?.children)) {
    return '';
  }
  return subject.children
    .map(child => (typeof child?.text === 'string' ? child.text : ''))
    .join('')
    .trim();
};

// Rich text fields flagged with {"ui:options": {"subjectRequired": true}}
// must have a non-empty subject line. An empty field is left to the JSON
// schema "required" validation.
const validateSubject = ({ name, formData, errors }) => {
  const value = formData?.[name];
  if (value && getSubjectText(value) === '') {
    errors[name].addError('must have a subject');
  }
};

const validationMap = {
  [WIDGET_TYPE_FILE_UPLOAD]: validateUpload
};
export const validate =
  (schema: RJSFSchema, uiSchema: UiSchema): CustomValidator =>
  (formData, errors) => {
    // !!! This is slowww
    Object.keys(uiSchema)
      .filter(x => x !== UI_ORDER)
      .forEach(formFieldName => {
        const required =
          schema.required && schema.required.includes(formFieldName);
        const fieldType = uiSchema[formFieldName][UI_FIELD];
        const validator = validationMap[fieldType];
        if (typeof validator === 'function')
          validator({ name: formFieldName, required, formData, errors });
        if (uiSchema[formFieldName][UI_OPTIONS]?.subjectRequired) {
          validateSubject({ name: formFieldName, formData, errors });
        }
      });
    return errors;
  };
